import { useState } from "react";
import { useI18n } from "../lib/i18n";
import { useCart } from "../lib/cart";

export default function ProductCard({ product, index = 0 }) {
  const { t, lang } = useI18n();
  const { addItem } = useCart();
  const sizes = product.sizes || ["S", "M", "L", "XL"];
  const [selectedSize, setSelectedSize] = useState(sizes[0]);
  const [adding, setAdding] = useState(false);

  const localized = product.i18n?.[lang] || {};
  const name = localized.name || product.name;
  const description = localized.description || product.description;
  const catLabel = t(`cat_${product.category}`) || product.category;

  const add = () => {
    setAdding(true);
    addItem(product, selectedSize);
    setTimeout(() => setAdding(false), 600);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative border border-zinc-900 bg-zinc-950 hover:border-green-400 transition-all duration-300 fade-up flex flex-col"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        <img
          src={product.image_url}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {product.featured && (
          <div className="absolute top-4 left-4 bg-green-500 text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">
            {t("featured_badge")}
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              {catLabel}
            </p>
            <h3
              data-testid={`product-name-${product.id}`}
              className="font-display uppercase text-xl tracking-tight text-white"
            >
              {name}
            </h3>
            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
              {description}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p
              data-testid={`product-price-${product.id}`}
              className="font-display text-xl text-green-400"
            >
              ${product.price.toFixed(0)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
              {product.stock > 0 ? t("stock_left", product.stock) : t("sold_out")}
            </p>
          </div>
        </div>

        {/* Size selector */}
        <div className="flex flex-wrap gap-1 mt-1">
          {sizes.map((s) => (
            <button
              key={s}
              onClick={() => setSelectedSize(s)}
              data-testid={`product-size-${product.id}-${s}`}
              className={`text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${
                selectedSize === s
                  ? "bg-white text-black border-white"
                  : "bg-transparent border-zinc-800 text-zinc-400 hover:border-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <button
          onClick={add}
          disabled={product.stock <= 0 || adding}
          data-testid={`product-add-${product.id}`}
          className="mt-auto bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest py-3 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {adding ? "Added ✓" : product.stock > 0 ? t("add_cart") : t("sold_out")}
        </button>
      </div>
    </div>
  );
}
